import { useState, type ReactNode } from "react";
import { IconBrandWhatsapp, IconMessage } from "@tabler/icons-react";
import { useI18n } from "../i18n";
import { Button } from "./ui";

/** Where a phone code goes. WhatsApp first, SMS on request, like the rider and driver apps. */
export type OtpChannel = "whatsapp" | "sms";

export const otherChannel = (c: OtpChannel): OtpChannel => (c === "whatsapp" ? "sms" : "whatsapp");

export function useOtpChannel() {
  return useState<OtpChannel>("whatsapp");
}

/** The form's main button before a code is sent. */
export function SendCodeButton({ channel, loading, disabled, onClick }: { channel: OtpChannel; loading?: boolean; disabled?: boolean; onClick?: () => void }) {
  const { t } = useI18n();
  return (
    <Button type={onClick ? "button" : "submit"} size="lg" block loading={loading} disabled={disabled} onClick={onClick}>
      {channel === "whatsapp" ? <IconBrandWhatsapp size={20} /> : <IconMessage size={20} />}
      {channel === "whatsapp" ? t("sendCodeWhatsApp") : t("sendCodeSms")}
    </Button>
  );
}

/** Under the send button: switch where the first code goes. */
export function ChannelSwitch({ channel, onChange, disabled }: { channel: OtpChannel; onChange: (c: OtpChannel) => void; disabled?: boolean }) {
  const { t } = useI18n();
  return (
    <button type="button" disabled={disabled} onClick={() => onChange(otherChannel(channel))} className="self-center text-sm font-medium text-brand hover:underline disabled:text-subtle">
      {channel === "whatsapp" ? t("useSmsInstead") : t("useWhatsAppInstead")}
    </button>
  );
}

export function codeSentText(t: ReturnType<typeof useI18n>["t"], channel: OtpChannel, phone: string) {
  return channel === "whatsapp" ? t("codeSentWhatsApp", { phone }) : t("smsCodeSent", { phone });
}

/** After a code is sent: resend the same way, or try the other channel (also a resend). */
export function ResendRow({
  channel,
  cooldownLeft,
  loading,
  onResend,
  leading,
}: {
  channel: OtpChannel;
  cooldownLeft: number;
  loading?: boolean;
  onResend: (c: OtpChannel) => void;
  leading?: ReactNode;
}) {
  const { t } = useI18n();
  const waiting = cooldownLeft > 0 || loading;
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-sm">
      {leading ?? <span />}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <button type="button" className="font-medium text-brand disabled:text-subtle" disabled={waiting} onClick={() => onResend(channel)}>
          {cooldownLeft > 0 ? t("resendIn", { s: cooldownLeft }) : t("resendCode")}
        </button>
        <button type="button" className="font-medium text-brand disabled:text-subtle" disabled={waiting} onClick={() => onResend(otherChannel(channel))}>
          {channel === "whatsapp" ? t("sendBySmsInstead") : t("sendOnWhatsAppInstead")}
        </button>
      </div>
    </div>
  );
}
